import React from "react";
import { isLoading, Loading } from "../../Loading";
import { AgencyId, PassportCategoryId } from "../../messages";
import {
  AgencyDocumentType,
  ExtendedAgencyDocumentCategory,
  ExtendedPassportDocumentCategory,
  PassportDocumentType,
} from "../../messages/UploadedDocuments";
import { OrientationLanguages } from "../../messages/Video";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import AppContainer from "../Generic/AppContainer";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import LoadingScaffold from "../LoadingScaffold";
import MedFlytPassportTermsApprovement from "../MedflytPassportTermsApprovement";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import AgencyCategory from "./components/AgencyCategory/AgencyCategory";
import PassportCategory from "./components/PassportCategory/PassportCategory";
import PassportExplainerCard from "./components/PassportExplainerCard/PassportExplainerCard";
import { TermStatus } from "./MedflytPassportActivity";

interface Props {
  categories: ExtendedPassportDocumentCategory[] | Loading;
  agenciesCategories: ExtendedAgencyDocumentCategory[] | Loading;
  language: OrientationLanguages | Loading;
  termStatus: TermStatus;
  onAcceptTerms: () => void;
  onBackClick: () => void;
  onClickDocumentType: (
    agncyId: AgencyId | null,
    documentType: PassportDocumentType | AgencyDocumentType,
    language: OrientationLanguages
  ) => void;
  onClickCatergoryHeader: (
    passportCategoryId: PassportCategoryId,
    contentRef: React.RefObject<HTMLDivElement>
  ) => void;
  onClickAgencyCatergoryHeader: (
    agencyId: AgencyId,
    contentRef: React.RefObject<HTMLDivElement>
  ) => void;
}

const MedflytPassportScreen = (props: Props) => {
  const {
    language,
    agenciesCategories,
    categories,
    onClickDocumentType,
    onClickCatergoryHeader,
    onClickAgencyCatergoryHeader,
  } = props;

  const handleClickPassportDocumentType = React.useCallback(
    (documentType: PassportDocumentType | AgencyDocumentType) => {
      if (!isLoading(language)) {
        onClickDocumentType(null, documentType, language);
      }
    },
    [language, onClickDocumentType]
  );

  const handleClickAgencyDocumentType = React.useCallback(
    (agncyId: AgencyId | null, documentType: PassportDocumentType | AgencyDocumentType) => {
      if (!isLoading(language)) {
        onClickDocumentType(agncyId, documentType, language);
      }
    },
    [language, onClickDocumentType]
  );

  const handleClickCategoryHeader = React.useCallback(
    (passportCategoryId: PassportCategoryId, contentRef: React.RefObject<HTMLDivElement>) => {
      onClickCatergoryHeader(passportCategoryId, contentRef);
    },
    [onClickCatergoryHeader]
  );

  const handleClickAgencyCatergoryHeader = React.useCallback(
    (agencyId: AgencyId, contentRef: React.RefObject<HTMLDivElement>) => {
      onClickAgencyCatergoryHeader(agencyId, contentRef);
    },
    [onClickAgencyCatergoryHeader]
  );

  if (isLoading(language) || isLoading(categories) || isLoading(agenciesCategories)) {
    return (
      <LoadingScaffold
        title={translate(STRINGS.MEDFLYT_PASSPORT)}
        onClickGoBack={props.onBackClick}
      />
    );
  }

  if (props.termStatus !== "APPROVED") {
    return (
      <MedFlytPassportTermsApprovement
        onApproveClick={props.onAcceptTerms}
        onCancelClick={props.onBackClick}
      />
    );
  }

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.MY_PASSPORT)} />}
      onClickGoBack={props.onBackClick}
    >
      <AppContainer style={{ marginBottom: 24 }}>
        <PassportExplainerCard />
      </AppContainer>

      <AppSlideFadeIn>
        {categories.map((category) => (
          <PassportCategory
            isContentOpen={category.isOpen}
            key={category.id.toString()}
            language={language}
            category={category}
            onClickPassportDocumentType={handleClickPassportDocumentType}
            onClickCatergoryHeader={handleClickCategoryHeader}
          />
        ))}

        {agenciesCategories.map((agencyCategory) => (
          <AgencyCategory
            isContentOpen={agencyCategory.isOpen}
            key={agencyCategory.agencyId.toString()}
            language={language}
            agencyCategory={agencyCategory}
            onClickAgencyDocumentType={handleClickAgencyDocumentType}
            onClickAgencyCatergoryHeader={handleClickAgencyCatergoryHeader}
          />
        ))}
      </AppSlideFadeIn>
    </Scaffold>
  );
};

export default React.memo(MedflytPassportScreen);
